/**
 * Wyldecrest Reports Dashboard
 * 
 * Client-facing view: "What's happening on our sites?"
 * Clean, reassuring, actionable
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Clock,
  PoundSterling, FileText, Building2, Briefcase, RefreshCw,
  ArrowRight, BarChart3, PieChart
} from 'lucide-react';
import axios from 'axios';
import { PHSCommandLayout } from '@/components/phs/enterprise';

const API = process.env.REACT_APP_BACKEND_URL;

// ============================================================
// 🎨 ENTERPRISE TEAL/SLATE DESIGN TOKENS
// ============================================================
const tokens = {
  primary: "#0f766e",      // teal-700
  bg: "#f8fafc",           // slate-50
  card: "#FFFFFF",
  textMain: "#0f172a",     // slate-900
  textMuted: "#64748b",    // slate-500
  border: "#e2e8f0",       // slate-200
  success: "#10B981",
  warning: "#F59E0B",
  error: "#EF4444",
};

// ============================================================
// 🧱 COMPONENTS
// ============================================================

const StatCard = ({ label, value, icon: Icon, trend, variant = "default", subtitle }) => {
  const variants = {
    default: { bg: "bg-white", text: "text-slate-900", icon: "bg-gray-100 text-gray-600" },
    success: { bg: "bg-emerald-50", text: "text-emerald-700", icon: "bg-emerald-100 text-emerald-600" },
    warning: { bg: "bg-amber-50", text: "text-amber-700", icon: "bg-amber-100 text-amber-600" },
    danger: { bg: "bg-red-50", text: "text-red-700", icon: "bg-red-100 text-red-600" },
    primary: { bg: "bg-teal-50", text: "text-teal-700", icon: "bg-teal-100 text-teal-700" },
  };

  const v = variants[variant];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      className={`${v.bg} border-2 border-[#E2E8F0] rounded-2xl p-5 shadow-sm hover:shadow-md transition-all`}
    >
      <div className="flex justify-between items-start">
        <div>
          <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wide">{label}</p>
          <motion.p 
            className={`text-3xl font-bold mt-1 ${v.text}`}
            key={value}
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
          >
            {value}
          </motion.p>
          {subtitle && <p className="text-xs text-[#64748B] mt-1">{subtitle}</p>}
          {trend !== undefined && (
            <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${trend >= 0 ? "text-emerald-600" : "text-red-600"}`}>
              {trend >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              {Math.abs(trend)}% vs last month
            </div>
          )}
        </div>
        {Icon && (
          <div className={`p-3 rounded-xl ${v.icon}`}>
            <Icon className="h-6 w-6" />
          </div>
        )}
      </div>
    </motion.div>
  );
};

const SectionHeader = ({ title, action }) => (
  <div className="flex justify-between items-center mb-4">
    <h2 className="text-lg font-bold text-[#0F172A]">{title}</h2>
    {action}
  </div>
);

const ProgressBar = ({ label, value, max, color = "#0f766e" }) => {
  const percent = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-[#64748B]">{label}</span>
        <span className="font-semibold text-[#0F172A]">£{value.toLocaleString()}</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percent}%` }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="h-full rounded-full"
          style={{ backgroundColor: color }}
        />
      </div>
    </div>
  );
};

// ============================================================
// 🎯 MAIN COMPONENT
// ============================================================

export default function WyldecrestReportsDashboard() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [costAnalysis, setCostAnalysis] = useState(null);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('wyldecrest_token') || localStorage.getItem('token');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [overviewRes, costRes] = await Promise.all([
        axios.get(`${API}/api/wyldecrest/reports/overview`, { headers }),
        axios.get(`${API}/api/wyldecrest/reports/cost-analysis`, { headers })
      ]);
      
      setOverview(overviewRes.data);
      setCostAnalysis(costRes.data);
    } catch (err) {
      console.error("Load error:", err);
      setError(err.response?.data?.detail || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <PHSCommandLayout>
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }}>
            <RefreshCw className="h-8 w-8 text-teal-600" />
          </motion.div>
        </div>
      </PHSCommandLayout>
    );
  }

  return (
    <PHSCommandLayout>
      <div className="min-h-screen bg-slate-50 p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Performance Dashboard</h1>
            <p className="text-sm text-slate-500">Last 30 days overview</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={loadData}
            className="p-2 rounded-xl bg-white border-2 border-slate-200 hover:border-teal-500 transition-colors"
          >
          <RefreshCw className={`h-5 w-5 text-slate-500 ${loading ? 'animate-spin' : ''}`} />
        </motion.button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Total Jobs"
          value={overview?.total_jobs || 0}
          icon={Briefcase}
          variant="default"
        />
        <StatCard
          label="In Progress"
          value={overview?.in_progress || 0}
          icon={Clock}
          variant="primary"
        />
        <StatCard
          label="Completed"
          value={overview?.completed || 0}
          icon={CheckCircle}
          variant="success"
          subtitle={`Last 30 days: ${overview?.completed_last_30_days || 0}`}
        />
        <StatCard
          label="Overdue"
          value={overview?.overdue || 0}
          icon={AlertTriangle}
          variant={overview?.overdue > 0 ? "danger" : "success"}
        />
      </div>

      {/* Financial Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          label="Total Spend"
          value={`£${(overview?.total_spend || 0).toLocaleString()}`}
          icon={PoundSterling}
          variant="default"
        />
        <StatCard
          label="Avg Variance"
          value={`${overview?.avg_variance_percent || 0}%`}
          icon={BarChart3}
          variant={overview?.avg_variance_percent > 10 ? "warning" : "success"}
          subtitle={`${overview?.over_quote_invoices || 0} over-quote invoices`}
        />
        <StatCard
          label="SLA Compliance"
          value={`${overview?.sla_compliance_percent || 100}%`}
          icon={CheckCircle}
          variant={overview?.sla_compliance_percent >= 90 ? "success" : "warning"}
          subtitle={`${overview?.sla_breached || 0} breaches`}
        />
      </div>

      {/* Spend Analysis */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* By Trade */}
        <div className="bg-white border-2 border-[#E2E8F0] rounded-2xl p-5">
          <SectionHeader title="Spend by Trade" />
          <div className="space-y-1">
            {costAnalysis?.spend_by_trade?.slice(0, 5).map((item, i) => (
              <ProgressBar
                key={item.trade}
                label={item.trade}
                value={item.spend}
                max={costAnalysis?.total_spend || item.spend}
                color={i === 0 ? "#0f766e" : i === 1 ? "#10B981" : "#64748B"}
              />
            ))}
            {(!costAnalysis?.spend_by_trade || costAnalysis.spend_by_trade.length === 0) && (
              <p className="text-sm text-slate-500">No data available</p>
            )}
          </div>
        </div>

        {/* By Site */}
        <div className="bg-white border-2 border-slate-200 rounded-2xl p-5">
          <SectionHeader title="Spend by Site" />
          <div className="space-y-1">
            {costAnalysis?.spend_by_site?.slice(0, 5).map((item, i) => (
              <ProgressBar
                key={item.site}
                label={item.site}
                value={item.spend}
                max={costAnalysis?.total_spend || item.spend}
                color={i === 0 ? "#F59E0B" : i === 1 ? "#10B981" : "#64748B"}
              />
            ))}
            {(!costAnalysis?.spend_by_site || costAnalysis.spend_by_site.length === 0) && (
              <p className="text-sm text-slate-500">No data available</p>
            )}
          </div>
        </div>
      </div>

      {/* Top Contractors */}
      <div className="bg-white border-2 border-slate-200 rounded-2xl p-5">
        <SectionHeader 
          title="Top Contractors by Spend"
          action={
            <span className="text-xs text-slate-500">
              Budget Efficiency: {costAnalysis?.budget_efficiency_percent || 100}%
            </span>
          }
        />
        <div className="space-y-3">
          {costAnalysis?.top_contractors?.slice(0, 5).map((item, i) => (
            <motion.div
              key={item.contractor}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
              className="flex items-center justify-between py-2 border-b border-slate-200 last:border-0"
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                  i === 0 ? "bg-teal-700" : i === 1 ? "bg-emerald-500" : "bg-gray-400"
                }`}>
                  {i + 1}
                </div>
                <span className="font-medium text-slate-900">{item.contractor}</span>
              </div>
              <span className="font-bold text-slate-900">£{item.spend.toLocaleString()}</span>
            </motion.div>
          ))}
          {(!costAnalysis?.top_contractors || costAnalysis.top_contractors.length === 0) && (
            <p className="text-sm text-slate-500 text-center py-4">No contractor data available</p>
          )}
        </div>
      </div>

      {/* Monthly Trend */}
      {costAnalysis?.monthly_trend && costAnalysis.monthly_trend.length > 0 && (
        <div className="bg-white border-2 border-slate-200 rounded-2xl p-5">
          <SectionHeader title="Monthly Spend Trend" />
          <div className="flex items-end gap-2 h-32">
            {costAnalysis.monthly_trend.map((month, i) => {
              const maxSpend = Math.max(...costAnalysis.monthly_trend.map(m => m.spend));
              const height = maxSpend > 0 ? (month.spend / maxSpend) * 100 : 0;
              
              return (
                <div key={month.month} className="flex-1 flex flex-col items-center">
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: `${height}%` }}
                    transition={{ delay: i * 0.05, duration: 0.3 }}
                    className="w-full bg-teal-700 rounded-t-lg min-h-[4px]"
                    title={`£${month.spend.toLocaleString()}`}
                  />
                  <span className="text-[10px] text-slate-500 mt-1 rotate-45 origin-left">
                    {month.month.slice(5)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      </div>
    </PHSCommandLayout>
  );
}
